import { ReturnOpportunityReadable } from '@crm/api'
import { OpportunityCardTasks } from '@crm/features/opportunities/components'
import { useAppNavigate } from '@crm/hooks/useAppNavigate'
import { paths } from '@crm/routes'
import { composePrice } from '@crm/utils'
import { getCustomFields } from '@crm/utils/getCustomFields'
import { DayLabel, TagsGroup } from '@shared/components'
import { useLocales } from '@shared/locales'
import { displayDate } from '@shared/utils'
import {
  Button,
  Card,
  Collapse,
  Descriptions,
  Divider,
  Flex,
  Typography
} from 'antd'
import { DescriptionsItemType } from 'antd/es/descriptions'
import { Edit2 } from 'iconsax-react'

import { AppLink } from '../AppLink'
import { OpportunityStatuses } from '../OpportunityStatuses'

type Props = {
  opportunity: ReturnOpportunityReadable
}

const descriptionStyles = {
  content: {
    fontWeight: 500
  }
}

export const OpportunityCard = ({ opportunity }: Props) => {
  const { t, currentLang } = useLocales()
  const navigate = useAppNavigate()

  const customFields = getCustomFields(
    opportunity?.custom_field_values,
    t,
    currentLang
  )

  const opportunityItems: DescriptionsItemType[] = [
    {
      label: t('task.title'),
      children: opportunity?.name
    },
    {
      label: t('contracts.value'),
      children: composePrice(
        opportunity?.value ?? '',
        opportunity?.currency?.symbol
      )
    },
    {
      label: t('task.product'),
      children: opportunity?.product?.name
    },
    {
      label: t('contacts.lead'),
      children: opportunity?.lead ? (
        <AppLink to={`/leads/${opportunity?.lead?.id}`}>
          {opportunity?.lead?.name}
        </AppLink>
      ) : (
        '-'
      )
    },
    {
      label: t('menu.customer'),
      children: opportunity?.client ? (
        <AppLink to={`/clients/${opportunity?.client?.id}`}>
          {opportunity?.client?.name}
        </AppLink>
      ) : (
        '-'
      )
    },
    {
      label: t('task.owner'),
      children: opportunity?.owner?.name
    },
    { label: t('refusal_reason'), children: opportunity?.reason?.name },
    {
      label: t('filter.created_at'),
      children: <DayLabel date={opportunity?.created_at} />
    },
    {
      label: t('task.close_date'),
      children: opportunity?.close_date
        ? displayDate(opportunity?.close_date)
        : '-'
    }
  ].filter(({ children }) => children)

  return (
    <Card>
      <Flex vertical gap={16}>
        <Flex align="center" justify="space-between" gap={16}>
          <OpportunityStatuses opportunity={opportunity} bordered />

          <Button
            type="text"
            icon={<Edit2 size={20} />}
            onClick={() =>
              navigate(
                paths.opportunitiesDetails.edit(
                  String(opportunity?.id),
                  String(opportunity?.lead?.id)
                )
              )
            }
          />
        </Flex>

        <Flex justify="center" className="w-full">
          <TagsGroup
            tags={opportunity?.tags ?? []}
            rows={3}
            showEmptyState={false}
            style={{ textAlign: 'center' }}
          />
        </Flex>
      </Flex>

      <Divider />

      <Descriptions
        title={t('opportunity.info')}
        size="small"
        layout="horizontal"
        bordered
        colon={false}
        column={1}
        items={opportunityItems}
        styles={descriptionStyles}
      />

      {!!customFields?.length && (
        <>
          <Divider />

          <Collapse
            size="small"
            ghost={true}
            expandIconPosition="end"
            items={[
              {
                key: 'customFields',
                label: (
                  <Typography.Text strong>
                    {t('nomenclature.custom_fields')}
                  </Typography.Text>
                ),
                styles: {
                  body: {
                    padding: 0
                  }
                },
                children: (
                  <Descriptions
                    size="small"
                    bordered
                    colon={false}
                    column={1}
                    items={customFields}
                  />
                )
              }
            ]}
          />
        </>
      )}

      <Divider />

      <OpportunityCardTasks opportunity={opportunity} />
    </Card>
  )
}
